// Deriving the CHILD assembly of a two-level BOM.
//
// Kevin, 2026-09-08: "a flame cell with a face plate will always have a flame cell with no
// face plate in its bom. some times we might make a cpq config for a cell with plate but the
// cell with plate and the cell without plate do not exist. you will have to build both items
// and both boms."
//
// buildAssembly marks a plated cell's BOM with NESTED_CHILD_ITEM and sets needsNestedChild
// (see ProductProfile.nestedChildWhen). It cannot resolve the child itself: that needs the
// operation pipeline re-run with the plate removed, which a pure builder does not do. This
// module is that re-run. Creating the child in NetSuite, and substituting its internal id,
// belongs to the caller.
import { runOperations } from "./operationRunner";
import { buildAssembly, NESTED_CHILD_ITEM, type AssemblyPreview } from "./assemblyBuilder";
import type { CpqData } from "../data/loader";
import type { ProductProfile } from "../products/profile";
import type { AnswerState, SelectedRowState, TableState } from "../types";

export type NestedChildInput = {
  /** The PARENT's fully resolved answers -- the child is the same configuration minus the plate. */
  answers: AnswerState;
  tables: TableState;
  selectedRows: SelectedRowState;
  data: CpqData;
  profile: ProductProfile;
  routings: Parameters<typeof buildAssembly>[1]["routings"];
};

/**
 * The child's answers, preview and part number, or null when the product declares no
 * nesting or the overlay would not actually remove the plate.
 *
 * WHY THE SCRATCHPAD IS PRE-ARMED. Operations that run once are guarded by
 * `scratchpad._ranOnce`, a Set of operation ids. Re-running a pipeline with an EMPTY
 * scratchpad re-fires every startup default and mangles the model -- that is how a housing
 * turns HBL into HL. The child derivation starts from answers that are already fully
 * resolved, so no startup default should fire at all: every operation id is marked as
 * already-run before the pipeline starts.
 */
export function deriveNestedChild(input: NestedChildInput): {
  answers: AnswerState;
  preview: AssemblyPreview;
  itemid: string;
} | null {
  const { answers, tables, selectedRows, data, profile, routings } = input;
  const overlay = profile.nestedChildOverlay;
  if (!overlay || Object.keys(overlay).length === 0) return null;

  const ranOnce = new Set<string>(
    (data.operations ?? []).map((op) => String((op as { id?: unknown }).id ?? ""))
  );
  const scratchpad: Record<string, unknown> = { _ranOnce: ranOnce };

  // Two passes, as everywhere else in this codebase: the first applies the overlay and the
  // second lets anything computed from it settle (the face-plate resolver at seq 0.5 feeds
  // the key builder further down the sequence).
  const once = runOperations(
    data.operations,
    {
      answers: { ...answers, ...overlay },
      tables,
      selectedRows,
      questions: data.questions,
      qtables: data.qtables,
      tabs: data.tabs,
      materials: data.materials,
      scratchpad,
      product: data.slug,
    }
  ).state;
  const settled = runOperations(
    data.operations,
    {
      answers: once.answers,
      tables: once.tables,
      selectedRows: once.selectedRows,
      questions: data.questions,
      qtables: data.qtables,
      tabs: data.tabs,
      materials: data.materials,
      scratchpad,
      product: data.slug,
    }
  ).state;

  const preview = buildAssembly(settled.answers, {
    itcrs: data.itcrs,
    materials: data.materials,
    routings,
    fingerprintKey: profile.fingerprintAnswerKey,
    computeRouting: profile.computeRouting,
    bomFallbackComponents: profile.bomFallbackComponents,
    expectedComponentFamilies: profile.expectedComponentFamilies,
    buildsRouting: profile.buildsRouting,
    nestedChildWhen: profile.nestedChildWhen,
    nestedKeepRowPrefixes: profile.nestedKeepRowPrefixes,
  });

  // THE CHILD MUST BE A LEAF. If the overlay failed to clear the plate, the child comes back
  // wanting a child of its own and the caller would recurse forever. Refuse instead.
  if (preview.needsNestedChild) return null;
  const itemid = String(preview.item?.itemid ?? "");
  if (!itemid) return null;
  return { answers: settled.answers, preview, itemid };
}

/** Replace the placeholder component with the child's real internal id. */
export function substituteNestedChild(
  preview: AssemblyPreview,
  childItemId: string | number
): AssemblyPreview {
  return {
    ...preview,
    bom: {
      ...preview.bom,
      components: preview.bom.components.map((c) =>
        c.item === NESTED_CHILD_ITEM ? { ...c, item: String(childItemId) } : c
      ),
    },
    needsNestedChild: false,
  };
}
